<template>
    <FullScreenModal :show="show" @close="closeLater">
        <div class="attendance-modal">
            <div class="modal-header">
                <i class="material-icons header-icon">how_to_reg</i>
                <h2>{{ isUpdate ? 'עדכון דיווח נוכחות' : 'אישור הגעת מתנדבים' }}</h2>
                <p class="request-info">{{ report.requestTitle }}</p>
                <p class="request-date">{{ formattedDate }}</p>
            </div>

            <div class="modal-body">
                <!-- Correction flow: the previous answer is pre-selected below -->
                <p v-if="isUpdate" class="update-note">
                    <i class="material-icons">info</i>
                    הדיווח שנשלח מוצג למטה - ניתן לתקן אותו ולשלוח מחדש.
                </p>

                <!-- One volunteer left to answer for: yes/no question -->
                <template v-if="editableList.length === 1">
                    <p class="question">
                        האם המתנדב/ת <strong>{{ editableList[0].name }}</strong> הגיע/ה אליך בפועל?
                    </p>
                    <div class="yes-no-buttons">
                        <button class="answer-btn yes" :class="{ selected: editableList[0].attended === true }"
                            @click="editableList[0].attended = true">
                            <i class="material-icons">check_circle</i>
                            כן
                        </button>
                        <button class="answer-btn no" :class="{ selected: editableList[0].attended === false }"
                            @click="editableList[0].attended = false">
                            <i class="material-icons">cancel</i>
                            לא
                        </button>
                    </div>
                </template>

                <!-- Several volunteers left to answer for: checklist -->
                <template v-else-if="editableList.length > 1">
                    <p class="question">סמן/י מי מהמתנדבים הגיע אליך בפועל:</p>
                    <div class="volunteers-checklist">
                        <label v-for="vol in editableList" :key="vol.volunteerId" class="volunteer-check-row">
                            <input type="checkbox" :checked="vol.attended === true"
                                @change="vol.attended = $event.target.checked" />
                            <span class="volunteer-name">{{ vol.name }}</span>
                        </label>
                    </div>
                </template>

                <!-- Every volunteer already handled - nothing to send -->
                <p v-else class="question">
                    הנוכחות של כל המתנדבים בדוח זה כבר טופלה על ידי הרכז, ולכן לא ניתן לעדכן אותו.
                </p>

                <!-- Hours input for hourly help types (editable volunteers only) -->
                <div v-if="isHourly && editableList.length" class="hours-section">
                    <template v-for="vol in editableList" :key="'hours-' + vol.volunteerId">
                        <div v-if="vol.attended === true" class="hours-row">
                            <label>מספר שעות ההתנדבות של {{ vol.name }}:</label>
                            <input type="number" min="0.5" step="0.5" v-model.number="vol.hours"
                                placeholder="לדוגמה: 2" />
                        </div>
                    </template>
                </div>

                <!-- Volunteers the coordinator already handled: read-only, kept visible for context -->
                <div v-if="lockedList.length" class="locked-section">
                    <p class="locked-section__title">מתנדבים שכבר טופלו על ידי הרכז:</p>
                    <div class="volunteers-checklist">
                        <div v-for="vol in lockedList" :key="'locked-' + vol.volunteerId"
                            class="volunteer-check-row volunteer-check-row--locked">
                            <i class="material-icons">lock</i>
                            <div class="locked-info">
                                <span class="volunteer-name">{{ vol.name }}</span>
                                <span class="locked-note">הנוכחות של מתנדב זה כבר טופלה{{ lockedStateSuffix(vol) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="editableList.length" class="details-section">
                    <label>פרטים נוספים (למשל מספר שעות, הערות):</label>
                    <textarea v-model="details" rows="3" placeholder="פרטים נוספים..."></textarea>
                </div>

                <div class="action-buttons">
                    <button v-if="editableList.length" class="submit-btn" :disabled="isSubmitting"
                        @click="submitReport">
                        {{ isUpdate ? 'שלח עדכון' : 'שלח' }}
                    </button>
                    <button class="later-btn" :disabled="isSubmitting" @click="closeLater">
                        {{ editableList.length ? 'אשיב מאוחר יותר' : 'סגור' }}
                    </button>
                </div>
            </div>
        </div>
    </FullScreenModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { doc, updateDoc, runTransaction, serverTimestamp, increment } from 'firebase/firestore'
import { db } from '@/firebase/config'
import FullScreenModal from '@/components/FullScreenModal.vue'
import { showModal } from '@/main_store/modal'
import { handleErrors } from '@/views/views_functions/errorHandler'
import { getHelpType, computeSuggestedPoints } from '@/constants/helpTypes'
import { isVolunteerLocked } from './attendanceLock'

const props = defineProps({
    show: { type: Boolean, default: false },
    report: { type: Object, required: true } // attendance-reports doc data + id
})

const emit = defineEmits(['close', 'submitted'])

const isSubmitting = ref(false)
const details = ref('')
const localVolunteers = ref([])

watch(() => props.report, (report) => {
    details.value = report?.details || ''
    // The lock is decided per volunteer - a coordinator crediting one name must not freeze the others
    localVolunteers.value = (report?.volunteers || []).map(vol => ({
        ...vol,
        locked: isVolunteerLocked(vol, report)
    }))
}, { immediate: true })

const editableList = computed(() => localVolunteers.value.filter(vol => !vol.locked))
const lockedList = computed(() => localVolunteers.value.filter(vol => vol.locked))

// Show what the coordinator has on record for a locked volunteer
const lockedStateSuffix = (vol) => {
    if (vol.attended === true) return ' - סומן כהגיע'
    if (vol.attended === false) return ' - סומן כלא הגיע'
    return ''
}

const isHourly = computed(() => {
    const ht = getHelpType(props.report?.helpType)
    return !!ht?.perHour
})

// Already answered once - opened again from MyRequests to correct the previous answer
const isUpdate = computed(() => props.report?.status === 'answered')

const formattedDate = computed(() => {
    const dateString = props.report?.requestDate
    if (!dateString) return ''
    const dateObject = new Date(dateString.replace(/\//g, '-'))
    if (isNaN(dateObject)) return dateString
    return `${dateObject.getDate()}/${dateObject.getMonth() + 1}/${dateObject.getFullYear()}`
})

const closeLater = () => {
    emit('close')
}

const submitReport = async () => {
    if (isSubmitting.value) return

    // Validation covers the editable volunteers only - locked ones are not being sent
    const answerable = editableList.value
    if (!answerable.length) {
        showModal('הנוכחות של כל המתנדבים בדוח זה כבר טופלה על ידי הרכז.', 'warning')
        return
    }

    // The yes/no UI (a single editable volunteer) demands an explicit answer; in the checklist an
    // unchecked box means "did not attend"
    for (const vol of answerable) {
        if (vol.attended === null || vol.attended === undefined) {
            if (answerable.length === 1) {
                showModal('יש לבחור כן או לא.', 'warning')
                return
            }
            vol.attended = false
        }
    }

    // Hourly help types require hours for volunteers who attended
    if (isHourly.value) {
        const missingHours = answerable.some(vol => vol.attended === true && (!vol.hours || vol.hours <= 0))
        if (missingHours) {
            showModal('יש להזין את מספר השעות עבור כל מתנדב שהגיע.', 'warning')
            return
        }
    }

    isSubmitting.value = true
    try {
        const answers = new Map(answerable.map(vol => [vol.volunteerId, vol]))
        const reportRef = doc(db, 'attendance-reports', props.report.id)

        // Merge inside a transaction against the stored document: a volunteer the coordinator
        // credited while this modal was open keeps their stored values, never ours
        await runTransaction(db, async (transaction) => {
            const reportSnapshot = await transaction.get(reportRef)
            if (!reportSnapshot.exists()) throw new Error('REPORT_NOT_FOUND')

            const storedReport = reportSnapshot.data()
            let updatedCount = 0

            const mergedVolunteers = (storedReport.volunteers || []).map(storedVol => {
                if (isVolunteerLocked(storedVol, storedReport)) return storedVol

                const answer = answers.get(storedVol.volunteerId)
                if (!answer) return storedVol

                updatedCount++
                const hours = answer.attended === true && isHourly.value ? answer.hours : answer.hours || null
                return {
                    ...storedVol,
                    attended: answer.attended,
                    hours: hours,
                    suggestedPoints: answer.attended === true
                        ? computeSuggestedPoints(storedReport.helpType, hours)
                        : null
                }
            })

            if (!updatedCount) throw new Error('NOTHING_TO_UPDATE')

            transaction.update(reportRef, {
                volunteers: mergedVolunteers,
                details: details.value,
                status: 'answered',
                answeredAt: serverTimestamp(),
                // Only the requester's submit bumps this - it is how the cloud function tells a real
                // answer/correction apart from a manager write (crediting, "mark handled")
                answerVersion: increment(1)
            })
        })

        try {
            await updateDoc(doc(db, 'volunteer-requests', props.report.requestId), {
                'attendanceCheck.status': 'answered',
                'attendanceCheck.answeredAt': serverTimestamp()
            })
        } catch (requestError) {
            // The report is the source of truth - a failed request-flag update shouldn't block the flow
            console.error('Failed to update request attendanceCheck:', requestError)
        }

        showModal(isUpdate.value ? 'הדיווח עודכן ונשלח לרכז.' : 'תודה! הדיווח נשלח לרכז.', 'success')
        emit('submitted')
    } catch (error) {
        if (error.message === 'REPORT_NOT_FOUND') {
            showModal('הדוח לא נמצא.', 'error')
        } else if (error.message === 'NOTHING_TO_UPDATE') {
            // The coordinator finished the remaining volunteers while the modal was open
            showModal('הנוכחות של כל המתנדבים בדוח זה כבר טופלה על ידי הרכז.', 'warning')
            emit('submitted')
        } else {
            handleErrors(error)
        }
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.attendance-modal {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    direction: rtl;
    text-align: right;
}

.modal-header {
    background: linear-gradient(135deg, #026aca 0%, #0288d1 100%);
    color: white;
    padding: 24px 20px;
    text-align: center;
}

.header-icon {
    font-size: 40px;
}

.modal-header h2 {
    margin: 8px 0 4px;
    font-size: 1.3rem;
}

.request-info {
    margin: 4px 0 0;
    font-weight: bold;
}

.request-date {
    margin: 2px 0 0;
    font-size: 0.9rem;
    opacity: 0.9;
}

.modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.question {
    font-size: 1.05rem;
    margin: 0;
}

.update-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 10px 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    color: #1e40af;
    font-size: 0.9rem;
}

.update-note i {
    font-size: 20px;
    flex-shrink: 0;
}

.yes-no-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.answer-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 24px;
    border: 2px solid #ccc;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    font-size: 1rem;
}

.answer-btn.yes.selected {
    border-color: #10b981;
    background: #d1fae5;
    color: #065f46;
}

.answer-btn.no.selected {
    border-color: #ef4444;
    background: #fee2e2;
    color: #991b1b;
}

.volunteers-checklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.volunteer-check-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
}

.volunteer-check-row input[type="checkbox"] {
    width: 18px;
    height: 18px;
}

.volunteer-name {
    font-size: 1rem;
}

/* Volunteers already handled by the coordinator - read-only */
.locked-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 4px;
    border-top: 1px solid #e5e7eb;
}

.locked-section__title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #64748b;
}

.volunteer-check-row--locked {
    align-items: flex-start;
    background: #f8fafc;
    border-color: #e2e8f0;
    cursor: default;
}

.volunteer-check-row--locked i {
    font-size: 18px;
    color: #94a3b8;
    margin-top: 2px;
}

.volunteer-check-row--locked .volunteer-name {
    color: #64748b;
}

.locked-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.locked-note {
    font-size: 0.8rem;
    color: #94a3b8;
}

.hours-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.hours-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.hours-row input {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
}

.details-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.details-section textarea {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    resize: vertical;
    font-family: inherit;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.submit-btn {
    background: linear-gradient(135deg, #026aca 0%, #0288d1 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px;
    font-size: 1.05rem;
    cursor: pointer;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.later-btn {
    background: none;
    border: none;
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.95rem;
    padding: 6px;
}
</style>
